// cloudIO — интеграция с облачным API CAD-проектов.
//
// Поверх KubikonCadService.js работает с store: при сохранении
// проставляет cloudProjectId, при открытии — заменяет документ через
// loadDocumentFromJson + ставит cloudProjectId.
//
// userId берётся из JWT в localStorage (как в SchemaService и других местах
// roboтехники: токен → jwt-decode → field 'sub' или 'user_id').

import { jwtDecode } from 'jwt-decode';

import useCadStore from './store/useCadStore';
import { serializeDocument, loadDocumentFromJson } from './documentIO';
import * as Cad from '../../API/KubikonCadService';

/** Достать user_id из JWT (как делается в остальном коде роботехники).
 *  Возвращает null если токена нет или decode не удался. */
export function getCurrentUserId() {
    try {
        const token = localStorage.getItem('Authorization');
        if (!token) return null;
        // Authorization-токен может быть как 'Bearer xxx' так и 'xxx'
        const raw = token.startsWith('Bearer ') ? token.slice(7) : token;
        const dec = jwtDecode(raw);
        // В разных проектах поле id зовётся по-разному — пробуем несколько
        const id = dec.sub ?? dec.user_id ?? dec.id ?? dec.userId;
        return Number.isFinite(Number(id)) ? Number(id) : null;
    } catch (_) {
        return null;
    }
}


/** Сохранить текущий документ в облако.
 *  Если cloudProjectId задан → PUT /projects/<id> (обновить).
 *  Иначе → POST /projects (создать) и записать новый id в store. */
export async function saveToCloud() {
    const userId = getCurrentUserId();
    if (!userId) throw new Error('Войди в аккаунт чтобы сохранять в облако');
    const store = useCadStore.getState();
    const doc = serializeDocument();
    const body = {
        user_id:       userId,
        title:         doc.title,
        document_data: JSON.stringify(doc),
        thumbnail:     '',  // TODO: рендер превью из Canvas (Месяц 9.2)
    };
    if (store.cloudProjectId) {
        const res = await Cad.updateProject(store.cloudProjectId, body);
        store.markSaved();
        return res.data;
    } else {
        const res = await Cad.createProject(body);
        const created = res.data;
        store.setCloudProjectId(created.id);
        store.markSaved();
        return created;
    }
}


/** Открыть проект из облака. Заменяет текущий документ на сервере. */
export async function openFromCloud(projectId) {
    const userId = getCurrentUserId();
    if (!userId) throw new Error('Войди в аккаунт чтобы открывать из облака');
    const res = await Cad.getProject(projectId, userId);
    const project = res.data;
    if (!project || !project.document_data) {
        throw new Error('Проект пуст или повреждён');
    }
    let parsed;
    try {
        parsed = JSON.parse(project.document_data);
    } catch (e) {
        throw new Error('Не удалось распарсить документ: ' + e.message);
    }
    // Подставляем title из БД (он мог быть переименован отдельно от документа)
    if (project.title) parsed.title = project.title;
    await loadDocumentFromJson(parsed);
    useCadStore.getState().setCloudProjectId(project.id);
}


/** Список моих проектов (для модалки «Мои проекты»). */
export async function listMyProjects() {
    const userId = getCurrentUserId();
    if (!userId) throw new Error('Войди в аккаунт');
    const res = await Cad.listUserProjects(userId);
    return res.data?.items || [];
}


/** Удалить проект в облаке. */
export async function deleteCloudProject(projectId) {
    const userId = getCurrentUserId();
    if (!userId) throw new Error('Войди в аккаунт');
    await Cad.deleteProject(projectId, userId);
    // Если удалили текущий открытый — сбрасываем cloudProjectId
    const store = useCadStore.getState();
    if (store.cloudProjectId === projectId) {
        store.setCloudProjectId(null);
    }
}
